import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { fetchAuthSession } from '@aws-amplify/auth';
import Question from './components/Question';
import TestTimer from './components/TestTimer';
import SERVICE_API_URL from './serviceUrl';

const StartTest = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]); // Questions for the current page
  const [answers, setAnswers] = useState(new Map()); // Store answers across all pages
  const [pastAnswers, setPastAnswers] = useState(new Map()); // Store past answers from the server
  const [nextPageToken, setNextPageToken] = useState(null);
  const [prevPageToken, setPrevPageToken] = useState(null);
  const [currentPageToken, setCurrentPageToken] = useState(null);
  const [isLastPage, setIsLastPage] = useState(false);
  const [isFirstPage, setIsFirstPage] = useState(true);
  const [prevPageTokens, setPrevPageTokens] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [testStatus, setTestStatus] = useState('not_started');
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [currentQuestionStartIndex, setCurrentQuestionStartIndex] = useState(0);
  const [testName, setTestName] = useState('');
  const [suggestedTimeInMinutes, setSuggestedTimeInMinutes] = useState(null);
  const [error, setError] = useState(null);

  // Fetch initial test status and questions
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        
        const response = await axios.get(`${SERVICE_API_URL}/prep/testId`, {
          params: {
            testId: testId,
            nextPageToken: null
          },
          headers: {
            "Authorization": `Bearer ${idToken}`
          }
        });

        setTotalQuestions(response.data.totalQuestions);
        setTestName(response.data.testName || '');
        setSuggestedTimeInMinutes(response.data.suggestedTimeInMinutes);

        // If we have answers or correctOption, the test is completed
        const isCompleted = response.data.questions.some(q => q.correctOption) || 
                          (response.data.answers && response.data.answers.length > 0);
        
        if (isCompleted) {
          setTestStatus('completed');
          // Process questions and answers for completed test
          handleCompletedTestData(response.data);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching test data:", error);
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [testId]);

  const handleCompletedTestData = (data) => {
    setQuestions(data.questions);
    setNextPageToken(data.nextPageToken);
    
    // Store past answers in a Map for easy lookup
    if (data.answers) {
      const answersMap = new Map();
      data.answers.forEach(answer => {
        answersMap.set(answer.questionId, answer.answeredOptionId);
      });
      setPastAnswers(answersMap);
    }
    
    if (data.nextPageToken != null) {
      prevPageTokens.set(data.nextPageToken, 1);
      prevPageTokens.set(1, null);
    }
    
    setCurrentPageToken(null);
    setPrevPageToken(null);
    setIsLastPage(data.nextPageToken === null);
    setIsFirstPage(true);
  };

  // When test status changes to in_progress, fetch questions
  useEffect(() => {
    if (testStatus === 'in_progress') {
      fetchQuestions(nextPageToken);
    }
  }, [testStatus]);

  // Fetch the questions for the given token
  const fetchQuestions = async (token) => {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    try {
      const response = await axios.get(`${SERVICE_API_URL}/prep/testId`, {
        params: {
          testId: testId,
          nextPageToken: token
        },
        headers: {
          "Authorization": `Bearer ${idToken}`
        }
      });

      setQuestions(response.data.questions);
      setNextPageToken(response.data.nextPageToken);
      
      // Update the question start index based on the token
      if (token === null) {
        setCurrentQuestionStartIndex(0);
      } else {
        setCurrentQuestionStartIndex(parseInt(token));
      }
      
      // Store past answers in a Map for easy lookup
      if (response.data.answers) {
        const answersMap = new Map();
        response.data.answers.forEach(answer => {
          answersMap.set(answer.questionId, answer.answeredOptionId);
        });
        setPastAnswers(answersMap);
      }
      
      if (response.data.nextPageToken != null) {
        if (token === null) {
          prevPageTokens.set(response.data.nextPageToken, 1);
          prevPageTokens.set(1, null);
        } else {
          prevPageTokens.set(response.data.nextPageToken, token);
        }
      }
      
      setCurrentPageToken(token);
      setPrevPageToken(token);
      setIsLastPage(response.data.nextPageToken === null);
      setIsFirstPage(token === null);
      setLoading(false);
      setError(null); // Clear any existing errors

      // Scroll to top after questions are loaded (works for both active and completed tests)
      requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      });
    } catch (error) {
      console.error("Error fetching questions:", error);
      setLoading(false);
      // Extract error message from the response
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error ||
                         "Failed to load test questions. Please try again later.";
      setError(errorMessage);
    }
  };

  const handleStartTest = () => {
    setTestStatus('in_progress');
  };

  const handleAnswer = (questionId, selectedOption) => {
    setAnswers(prevAnswers => {
      const updatedAnswers = new Map(prevAnswers);
      // If the clicked option is already the selected answer, deselect it.
      if (updatedAnswers.get(questionId) === selectedOption.optionId) {
        updatedAnswers.delete(questionId);
      } else {
        // Otherwise, select the new option.
        updatedAnswers.set(questionId, selectedOption.optionId);
      }
      return updatedAnswers;
    });
  };

  const handleTestSubmit = async () => {
    const allAnswers = Array.from(answers.entries()).map(([questionId, answeredOptionId]) => ({
      questionId,
      answeredOptionId,
    }));

    console.log("All answers: ", allAnswers);
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();  
      await axios.post(`${SERVICE_API_URL}/prep/submit-answers`,
        {
          testIdentifier: testId,
          answers: allAnswers
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`
          }
        }
      );

      console.log("All answers submitted:", allAnswers);
      // Clean up timer on test completion
      sessionStorage.removeItem('testStartTime');
      setTestStatus('completed');
      
      // Navigate to home page with a flag indicating test completion
      navigate('/', { state: { showYouTubeMessage: true, testName: testName } });
    } catch (error) {
      console.error("Error submitting response:", error);
      alert("Failed to submit response");
    }
  };

  // Scroll to top when questions change or when navigating pages
  useEffect(() => {
    if (questions.length > 0 && (testStatus === 'in_progress' || testStatus === 'completed')) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      });
    }
  }, [questions, testStatus, currentPageToken, nextPageToken]);

  const handleNextPage = () => {
    if (nextPageToken) {
      fetchQuestions(nextPageToken);
    }
  };

  const handlePrevPage = () => {
    const prevPageToken = prevPageTokens.get(currentPageToken);
    console.log("Previous page: ", prevPageToken);
    if (prevPageToken === 1) {
      fetchQuestions(null);
    } else {
      fetchQuestions(prevPageToken);
    }
  };

  const getAnswerButtonStyle = (question, option) => {
    const isSelected = answers.get(question.questionId) === option.optionId;
    const pastAnswer = pastAnswers.get(question.questionId);
    const isCorrect = question.correctOption === option.optionId;
    
    // If there's a past answer and we have the correct answer
    if (pastAnswer && question.correctOption) {
      if (pastAnswer === option.optionId) {
        // This was the user's answer
        return {
          backgroundColor: isCorrect ? 'var(--success)' : 'var(--error)',
          color: 'white',
          border: '2px solid ' + (isCorrect ? 'var(--success)' : 'var(--error)')
        };
      } else if (isCorrect) {
        // This was the correct answer
        return {
          backgroundColor: 'var(--success)',
          color: 'white',
          border: '2px solid var(--success)'
        };
      }
      return {
        backgroundColor: 'white',
        color: 'var(--text-color)',
        border: '2px solid var(--primary-color)'
      };
    }
    
    // For questions without past answers (new attempt)
    return {
      textAlign: 'left',
      backgroundColor: isSelected ? 'var(--primary-color)' : 'white',
      color: isSelected ? 'white' : 'var(--text-color)',
      border: '2px solid var(--primary-color)'
    };
  };

  // Calculate progress percentage
  const getProgress = () => {
    const answeredCount = answers.size + pastAnswers.size;
    return totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;
  };

  if (loading && testStatus === 'in_progress') {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <h2>Loading questions...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="card" style={{ 
          textAlign: 'center', 
          padding: '40px',
          backgroundColor: 'var(--error-light)',
          border: '1px solid var(--error)'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--spacing-md)'
          }}>
            <h2 style={{ color: 'var(--error)', marginBottom: 'var(--spacing-sm)' }}>
              Error Loading Test
            </h2>
            <p style={{ color: 'var(--text-primary)' }}>{error}</p>
            <button 
              onClick={() => navigate('/my-tests')}
              className="button"
              style={{ marginTop: 'var(--spacing-md)' }}
            >
              Back to Tests
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (testStatus === 'not_started') {
    return (
      <div className="container">
        <div className="card">
          {/* Enhanced Test Header */}
          <div style={{
            borderBottom: '1px solid var(--border-color)',
            marginBottom: 'var(--spacing-xl)',
            paddingBottom: 'var(--spacing-lg)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-md)',
              marginBottom: 'var(--spacing-sm)'
            }}>
              <h2 style={{ 
                margin: 0,
                color: 'var(--primary-color)',
                fontSize: 'var(--font-size-2xl)'
              }}>
                {testName}
              </h2>
              <div style={{
                display: 'flex',
                gap: 'var(--spacing-sm)',
                alignItems: 'center'
              }}>
                <span className="badge" style={{ backgroundColor: 'var(--primary-color)' }}>
                  {totalQuestions} Questions
                </span>
                <span className="badge" style={{ backgroundColor: 'var(--secondary-color)' }}>
                  {suggestedTimeInMinutes} Minutes
                </span>
              </div>
            </div>
            <p style={{ 
              margin: 0,
              color: 'var(--text-secondary)',
              fontSize: 'var(--font-size-md)'
            }}>
              Ready to begin your assessment? Take your time to read the instructions below.
            </p>
          </div>

          <div style={{ marginBottom: '30px' }}>
            <h3>Test Instructions:</h3>
            <ul style={{ lineHeight: '1.6' }}>
              <li>Read each question carefully.</li>
              <li>You can navigate between pages using Previous and Next buttons.</li>
              <li>Submit the test only when you have answered all questions.</li>
              <li>You cannot change answers after submission.</li>
              <li>The timer shows your progress, but you can keep going even after time runs out.</li>
            </ul>
          </div>
          <button onClick={handleStartTest} className="button">
            Start Test
          </button>
        </div>
      </div>
    );
  }

  if (testStatus === 'completed') {
    return (
      <div className="container">
        {/* Enhanced Results Header */}
        <div style={{ 
          position: 'sticky', 
          top: 0, 
          background: 'white', 
          padding: '15px 0', 
          borderBottom: '1px solid var(--border-color)',
          zIndex: 1000
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 'var(--spacing-md)'
          }}>
            <div>
              <h2 style={{ 
                margin: 0,
                marginBottom: 'var(--spacing-xs)',
                color: 'var(--primary-color)',
                fontSize: 'var(--font-size-2xl)'
              }}>
                {testName}
              </h2>
              <span className="badge" style={{ 
                backgroundColor: 'var(--primary-color)',
                fontSize: 'var(--font-size-md)'
              }}>
                {totalQuestions} Questions
              </span>
            </div>
            <button 
              onClick={() => navigate('/my-tests')} 
              className="button secondary"
            >
              Back to Tests
            </button>
          </div>
        </div>

        {/* Questions Section */}
        <div className="card" style={{ margin: '20px 0' }}>
          {questions.map((question, index) => (
            <Question
              key={question.questionId}
              question={question}
              onAnswer={() => {}}
              selectedAnswer={pastAnswers.get(question.questionId)}
              isCompleted={true}
              getAnswerButtonStyle={getAnswerButtonStyle}
              questionNumber={currentQuestionStartIndex + index + 1}
            />
          ))}
        </div>

        {/* Navigation Buttons */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          marginTop: '20px'
        }}>
          <button 
            onClick={handlePrevPage}
            className="button secondary"
            disabled={isFirstPage}
          >
            Previous Page
          </button>
          <button 
            onClick={handleNextPage}
            className="button"
            disabled={isLastPage}
          >
            Next Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Enhanced Active Test Header */}
      <div style={{ 
        position: 'sticky', 
        top: 0, 
        background: 'white', 
        padding: '15px 0', 
        borderBottom: '1px solid var(--border-color)',
        zIndex: 1000
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: 'var(--spacing-md)'
        }}>
          <div>
            <h2 style={{ 
              margin: 0,
              marginBottom: 'var(--spacing-xs)',
              color: 'var(--primary-color)',
              fontSize: 'var(--font-size-2xl)'
            }}>
              {testName || 'Loading...'}
            </h2>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              color: 'var(--text-secondary)',
              fontSize: 'var(--font-size-md)'
            }}>
              {answers.size + pastAnswers.size} of {totalQuestions} questions answered ({getProgress()}%)
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            {testStatus === 'in_progress' && suggestedTimeInMinutes && (
              <TestTimer suggestedTimeInMinutes={suggestedTimeInMinutes} />
            )}
            <button 
              onClick={() => setShowConfirmSubmit(true)} 
              className="button secondary"
              disabled={questions.some(q => q.correctOption || pastAnswers.has(q.questionId))}
            >
              Submit Test
            </button>
          </div>
        </div>
        <div className="progress-bar">
          <div 
            className="progress-bar-fill" 
            style={{ 
              width: `${getProgress()}%`,
              transition: 'width 0.3s ease'
            }}
          />
        </div>
      </div>

      {/* Questions Section */}
      <div className="card" style={{ margin: '20px 0' }}>
        {questions.map((question, index) => (
          <Question
            key={question.questionId}
            question={question}
            onAnswer={handleAnswer}
            selectedAnswer={answers.get(question.questionId)}
            isCompleted={testStatus === 'completed'}
            getAnswerButtonStyle={getAnswerButtonStyle}
            questionNumber={currentQuestionStartIndex + index + 1}
          />
        ))}
      </div>

      {/* Navigation Buttons */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        marginTop: '20px'
      }}>
        <button 
          onClick={handlePrevPage}
          className="button secondary"
          disabled={isFirstPage}
        >
          Previous Page
        </button>
        <button 
          onClick={handleNextPage}
          className="button"
          disabled={isLastPage}
        >
          Next Page
        </button>
      </div>

      {/* Submit Confirmation Dialog */}
      {showConfirmSubmit && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ 
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center'
          }}>
            <h3>Submit Test?</h3>
            {(answers.size + pastAnswers.size) < totalQuestions && (
              <p style={{ 
                color: 'var(--text-secondary)',
                margin: 'var(--spacing-md) 0'
              }}>
                You have {totalQuestions - (answers.size + pastAnswers.size)} unanswered questions. Unanswered questions will be marked as incorrect.
              </p>
            )}
            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              justifyContent: 'center',
              marginTop: '20px'
            }}>
              <button 
                onClick={() => setShowConfirmSubmit(false)}
                className="button secondary"
              >
                Continue Test
              </button>
              <button 
                onClick={handleTestSubmit}
                className="button"
              >
                Submit Test
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StartTest;